import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import PageBase from 'components/PageBase';
import {
  Button,
  Typography,
  MenuItem,
  Select
} from '@material-ui/core';
import useRouter from 'utils/useRouter';
import {
  ROLE_INTER_CLIENT
} from 'config';
import * as API from 'services/api';
import { setLoading } from 'redux/actions/app';
import useHomeContext from 'layouts/HomeLayout/context'
import Tabs from './tab'
import useStyles from '../styles';
import { PERPAGE } from 'config';
import { columnCreatedAt, columnId, filterTableData, exportUsersHeader, columnUser, columnActions, columnLoginInfo } from 'pages/common';
import ActionGroup from 'components/ActionGroup';
import ExportCSV from 'components/ExportCSV';
import DataTable from 'components/PageBase/dataTable';
import { showMessage } from 'utils';
import usePagedTable from 'hooks/usePagedTable';

const Users = ({ intl: { formatMessage } }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { /*hasPermission,*/ onDeleteById, setImportUsersModalVisible } = useHomeContext();
  const router = useRouter();

  const [clientId, setClientId] = useState(-1);

  const [openFilter, setOpenFilter] = useState(false)
  const [filters, setFilters] = useState({})
  const [keyword, setKeyword] = useState('')
  const [clients, setClients] = useState([]);

  useEffect(() => {
    Promise.all([API.listClients()])
      .then(([clientsInfo]) => {
        if (clientsInfo.code === 200) {
          setClients(clientsInfo.data);
        }
      })
      .catch(err => {

      });
  }, []);

  const toggleUsersImportModal = () => {
    setImportUsersModalVisible({
      addFree: true,
      apiName: "importUsers",
      needRole: true
    });
  };

  const {
    data,
    items: users,
    counts,
    loading: usersLoading,
    allLoaded: allUsersLoaded,
    exportType,
    setExportType,
    loadMore,
    loadExportData,
    reload,
  } = usePagedTable({
    fetchList: API.listUsers,
    fetchExport: API.listUsers,
    deps: [clientId, keyword],
    buildParams: (page) => {
      const params = {
        page,
        perPage: PERPAGE,
        ...keyword && keyword.length > 0 ? { keyword } : {}
      };
      if (clientId !== -1) {
        params.clientId = clientId;
      }
      return params;
    },
    selectList: res => res.users,
    selectCount: res => res.count,
  });
  const onAdd = () => {
    router.history.push('/users/create');
  };

  const onSaveUser = (id, status) => {
    dispatch(setLoading(true));
    API.updateUser(id, { status })
      .then(res => {
        dispatch(setLoading(false));
        if (res.code === 200) {
          reload();
          showMessage({
            message: 'User is updated successfully',
            type: 'success',
          });
        }
      })
      .catch(err => {
        dispatch(setLoading(false));
      });
  };

  const tableColumns = [
    columnId(formatMessage),
    {
      name: 'role',
      label: formatMessage({ id: 'role' }),
      options: { filter: true, sort: true, customBodyRender: value => value?.toUpperCase() },
    },
    ...columnUser(formatMessage),
    {
      name: 'clientId',
      label: formatMessage({
        id: 'company',
      }),
      options: {
        filter: true,
        sort: true,
        customBodyRender: useCallback((value) => {
          const client = clients.find(e => e.id === value);
          return <Typography style={{ textAlign: "center", minWidth: 120 }}><strong>{formatMessage({ id: client?.role == ROLE_INTER_CLIENT ? "company.international" : "company.client" })}</strong><br />{(client?.company || '')}</Typography>
        }, []),
        customFilterList: clients.map(item => ({ value: item.id, label: item.company }))
      },
    },
    {
      name: 'status',
      label: formatMessage({ id: 'status' }),
      options: {
        filter: true,
        sort: true,
        customBodyRender: useCallback((value, tableMeta, updateValue) => (
          <FormattedMessage id={`home.users.${value.toLowerCase()}`} />
        ), []),
      },
    },
    {
      name: 'selfSignup',
      label: "Création Autonome",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta, updateValue) => (
          value ? "Oui" : "Non"
        ),
      },
    },
    ...columnLoginInfo(formatMessage),
    {
      name: 'status',
      label: formatMessage({ id: 'status' }),
      options: {
        filter: true,
        sort: true,
        customBodyRender: useCallback((value, tableMeta, updateValue) => (
          <Select
            value={value || ''}
            onChange={e => onSaveUser(tableMeta.rowData[0], e.target.value)}
            variant='outlined'
          >
            <MenuItem value="Registered">
              <FormattedMessage id="home.users.registered" />
            </MenuItem>
            <MenuItem value="Invitation">
              <FormattedMessage id="home.users.Invitation" />
            </MenuItem>
            <MenuItem value="Confirmed">
              <FormattedMessage id="home.users.confirmed" />
            </MenuItem>
          </Select>
        ), [])
      },
    },
    columnCreatedAt(formatMessage),
    columnActions(
      useCallback((value, tableMeta, updateValue) => {
        const record = users.find(item => item.id == tableMeta.rowData[0])
        return (
          <ActionGroup
            onDelete={record.role != "client" ? () => {
              onDeleteById(tableMeta.rowData[0]);
            } : null}
            onEdit={() => {
              router.history.push(`/user`, { id: tableMeta.rowData[0] });
            }}
          />
        )
      }, [users])
    )
  ];


  const datasource = filterTableData(users, filters);

  return (
    <PageBase title={formatMessage({ id: 'navbar.externalusers' })}
      rightComponent={<div className={classes.rightdom}>
        <Button
          onClick={toggleUsersImportModal}
          classes={{ root: classes.excel }}
        >
          <FormattedMessage id="import_users" />
        </Button>
        <Button onClick={onAdd} classes={{ root: classes.excel }}>
          <FormattedMessage id="home.user.create" />
        </Button>
        <ExportCSV
          filename="users"
          headers={exportUsersHeader(formatMessage)}
          data={data}
          loadExportData={loadExportData}
          exportType={exportType}
          setExportType={setExportType}
        />
      </div>}>
      <div>
        <Tabs value={"france"} />
        <DataTable
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
          setOnFilter={e => {
            let newparam = { ...e }
            setClientId(newparam.clientId ? newparam.clientId : -1)
            delete newparam.clientId
            setFilters(newparam)
          }}
          keyword={keyword}
          setKeyword={setKeyword}
          loadMore={loadMore}
          allUsersLoaded={allUsersLoaded}
          tableColumns={tableColumns}
          datasource={datasource}
          data={users}
          counts={counts}
        />
      </div>
    </PageBase>
  );
};

Users.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }),
};

export default injectIntl(Users);
